import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Container, Row, Col, Card, CardBody, CardTitle,
    Button, Input, InputGroup, InputGroupText,
    Label, Table, Spinner, Badge, Alert
} from "reactstrap";
import ReactApexChart from "react-apexcharts";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAICostReport, getAIHealth, getAIHealthDeep, getAllUsersLookup } from "../../helpers/ai_api";

// ── Tarih helper'ları ──
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

const fmtUsd = (v) => "$" + Number(v || 0).toFixed(4);
const fmtTry = (v, rate) => rate ? "₺" + (Number(v || 0) * rate).toFixed(2) : "—";

function fetchUsdTryRate() {
    return fetch("https://open.er-api.com/v6/latest/USD")
        .then(resp => resp.json())
        .then(data => (data && data.rates && data.rates.TRY ? Number(data.rates.TRY) : null))
        .catch(() => null);
}

export default function AICostDashboard() {
    const [from, setFrom]               = useState(daysAgoISO(30));
    const [to, setTo]                   = useState(todayISO());
    const [selectedUser, setSelectedUser] = useState(null);   // {id, name, email} | null
    const [userSearch, setUserSearch]   = useState("");
    const [showUserList, setShowUserList] = useState(false);
    const [allUsers, setAllUsers]       = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const [report, setReport]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);
    const [rate, setRate]       = useState(null);

    const [health, setHealth]         = useState(null);
    const [healthDeep, setHealthDeep] = useState(null);
    const [hdLoading, setHdLoading]   = useState(false);

    const userDropdownRef = useRef(null);

    // Dropdown dışına tıklayınca listeyi kapat
    useEffect(() => {
        function handleDocClick(e) {
            if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
                setShowUserList(false);
            }
        }
        document.addEventListener("mousedown", handleDocClick);
        return () => document.removeEventListener("mousedown", handleDocClick);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAICostReport(from, to, selectedUser?.id);
            setReport(data);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "Yükleme hatası");
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [from, to, selectedUser]);

    useEffect(() => { load(); }, []); // ilk yükleme

    useEffect(() => {
        fetchUsdTryRate().then(setRate).catch(() => setRate(null));
    }, []);

    useEffect(() => {
        getAIHealth().then(setHealth).catch(() => setHealth({ ok: false }));
    }, []);

    // Kullanıcı listesini bir kez çek
    useEffect(() => {
        setUsersLoading(true);
        getAllUsersLookup()
            .then(setAllUsers)
            .catch(() => setAllUsers([]))
            .finally(() => setUsersLoading(false));
    }, []);

    // ID → ad lookup map (top_users tablosunda ad göstermek için)
    const userNameById = useMemo(() => {
        const m = {};
        allUsers.forEach(u => { m[String(u.id)] = u.name; });
        return m;
    }, [allUsers]);

    // Search filtresi — yazılan harfler eşleşirse filtrele, yoksa tümünü göster (scroll ile)
    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return allUsers;
        return allUsers.filter(u =>
            u.name.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q) ||
            String(u.id).includes(q)
        );
    }, [userSearch, allUsers]);

    const pickUser = (u) => {
        setSelectedUser(u);
        setUserSearch("");
        setShowUserList(false);
    };

    const clearUser = () => {
        setSelectedUser(null);
        setUserSearch("");
        setShowUserList(false);
    };

    const runDeepHealth = async () => {
        setHdLoading(true);
        try {
            const r = await getAIHealthDeep();
            setHealthDeep(r);
        } catch (e) {
            setHealthDeep({ ok: false, error: e?.response?.data?.message || e.message });
        } finally {
            setHdLoading(false);
        }
    };

    // Apex line chart — günlük tüketim
    const dailyChart = (() => {
        if (!report) return null;
        const days = Object.keys(report.by_day || {}).sort();
        const series = [{
            name: "USD",
            data: days.map(d => Number((report.by_day[d] || 0).toFixed(4))),
        }];
        const options = {
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            stroke: { curve: "smooth", width: 3 },
            dataLabels: { enabled: false },
            xaxis: { categories: days, labels: { rotate: -45 } },
            yaxis: { labels: { formatter: (v) => "$" + v.toFixed(3) } },
            colors: ["#556ee6"],
            tooltip: {
                y: {
                    formatter: (v) => {
                        const usd = "$" + v.toFixed(4);
                        const tryy = rate ? " (₺" + (v * rate).toFixed(2) + ")" : "";
                        return usd + tryy;
                    }
                }
            },
        };
        return { series, options };
    })();

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="AI" breadcrumbItem="Cost Dashboard" />

                {/* ── Kur bilgisi ── */}
                <Row className="mb-2">
                    <Col className="text-end">
                        {rate ? (
                            <Badge color="info" className="fs-6">
                                1 USD = ₺{rate.toFixed(2)}
                            </Badge>
                        ) : (
                            <Spinner size="sm" color="info" />
                        )}
                    </Col>
                </Row>

                {/* ── Sağlık durumu ── */}
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                                <Row className="align-items-center">
                                    <Col md="3">
                                        <h6 className="mb-1">Backend Health (shallow)</h6>
                                        {health == null && <Spinner size="sm" />}
                                        {health && health.ok && <Badge color="success">OK</Badge>}
                                        {health && !health.ok && <Badge color="danger">DOWN</Badge>}
                                        {health?.engine_version && (
                                            <div className="text-muted small mt-1">v{health.engine_version}</div>
                                        )}
                                    </Col>
                                    <Col md="6">
                                        <h6 className="mb-1">Deep Health (Anthropic + Deepgram)</h6>
                                        {!healthDeep && <span className="text-muted">Henüz çalıştırılmadı.</span>}
                                        {healthDeep && (
                                            <div>
                                                {Object.entries(healthDeep.checks || {}).map(([k, v]) => (
                                                    <Badge key={k}
                                                        color={v.ok ? "success" : "danger"}
                                                        className="me-2">
                                                        {k}: {v.ok ? "ok" : "fail"}
                                                        {v.latency_ms != null && ` (${v.latency_ms}ms)`}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md="3" className="text-end">
                                        <Button color="primary" size="sm" onClick={runDeepHealth} disabled={hdLoading}>
                                            {hdLoading ? <Spinner size="sm" /> : "Deep Check"}
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ── Filtre ── */}
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                                <Row className="align-items-end">
                                    <Col md="3">
                                        <Label className="form-label">Başlangıç</Label>
                                        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} />
                                    </Col>
                                    <Col md="3">
                                        <Label className="form-label">Bitiş</Label>
                                        <Input type="date" value={to} onChange={e => setTo(e.target.value)} />
                                    </Col>
                                    <Col md="4" style={{ position: "relative" }}>
                                        <Label className="form-label">
                                            Kullanıcı {usersLoading && <Spinner size="sm" />}
                                        </Label>
                                        {selectedUser ? (
                                            <div className="d-flex align-items-center">
                                                <Badge color="info" className="me-2 p-2">
                                                    #{selectedUser.id} — {selectedUser.name}
                                                </Badge>
                                                <Button size="sm" color="light" onClick={clearUser}>
                                                    Temizle
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <InputGroup>
                                                    <Input
                                                        type="text"
                                                        placeholder="Tümü — ad/email/ID ile ara…"
                                                        value={userSearch}
                                                        onChange={e => { setUserSearch(e.target.value); setShowUserList(true); }}
                                                        onFocus={() => setShowUserList(true)}
                                                    />
                                                    <InputGroupText
                                                        style={{ cursor: "pointer", userSelect: "none" }}
                                                        onMouseDown={(e) => { e.preventDefault(); setShowUserList(s => !s); }}
                                                    >
                                                        {showUserList ? "▲" : "▼"}
                                                    </InputGroupText>
                                                </InputGroup>
                                                {showUserList && (
                                                    <div
                                                        ref={userDropdownRef}
                                                        className="border bg-white"
                                                        style={{
                                                            position: "absolute",
                                                            top: "100%", left: 12, right: 12,
                                                            zIndex: 1000,
                                                            maxHeight: 280, overflowY: "auto",
                                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                            borderRadius: 4,
                                                        }}
                                                    >
                                                        {filteredUsers.map(u => (
                                                            <div
                                                                key={u.id}
                                                                onMouseDown={() => pickUser(u)}
                                                                style={{
                                                                    padding: "8px 12px",
                                                                    cursor: "pointer",
                                                                    borderBottom: "1px solid #eee",
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                                                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                                            >
                                                                <div className="fw-semibold">#{u.id} — {u.name}</div>
                                                                {u.email && <div className="text-muted small">{u.email}</div>}
                                                            </div>
                                                        ))}
                                                        {filteredUsers.length === 0 && (
                                                            <div className="text-muted small p-2 text-center">
                                                                {usersLoading ? "Yükleniyor…" : "Kullanıcı bulunamadı"}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Col>
                                    <Col md="2" className="text-end">
                                        <Button color="primary" onClick={load} disabled={loading}>
                                            {loading ? <Spinner size="sm" /> : "Yükle"}
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {error && <Alert color="danger">{error}</Alert>}

                {report && (
                    <>
                        {/* ── Özet kartları ── */}
                        <Row>
                            <Col md="3">
                                <Card><CardBody>
                                    <h5 className="text-muted">Toplam (Grand)</h5>
                                    <h3 className="text-primary">{fmtUsd(report.totals.grand_total_usd)}</h3>
                                    <small className="text-success">{fmtTry(report.totals.grand_total_usd, rate)}</small>
                                </CardBody></Card>
                            </Col>
                            <Col md="3">
                                <Card><CardBody>
                                    <h5 className="text-muted">Evaluate Çağrı</h5>
                                    <h3>{report.totals.evaluate_calls}</h3>
                                    <small className="text-muted">{fmtUsd(report.totals.evaluate_usd)}</small>
                                    <br />
                                    <small className="text-success">{fmtTry(report.totals.evaluate_usd, rate)}</small>
                                </CardBody></Card>
                            </Col>
                            <Col md="3">
                                <Card><CardBody>
                                    <h5 className="text-muted">Simülasyon</h5>
                                    <h3>{report.totals.sim_runs}</h3>
                                    <small className="text-muted">Özet: {fmtUsd(report.totals.sim_summary_usd)}</small>
                                    <br />
                                    <small className="text-success">{fmtTry(report.totals.sim_summary_usd, rate)}</small>
                                </CardBody></Card>
                            </Col>
                            <Col md="3">
                                <Card><CardBody>
                                    <h5 className="text-muted">Tekil Kullanıcı</h5>
                                    <h3>{report.totals.unique_users}</h3>
                                </CardBody></Card>
                            </Col>
                        </Row>

                        {/* ── Günlük grafik ── */}
                        {dailyChart && Object.keys(report.by_day).length > 0 && (
                            <Row>
                                <Col lg="12">
                                    <Card>
                                        <CardBody>
                                            <CardTitle className="mb-4">Günlük Tüketim (USD)</CardTitle>
                                            <ReactApexChart
                                                options={dailyChart.options}
                                                series={dailyChart.series}
                                                type="line"
                                                height={300}
                                            />
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {/* ── Breakdown tabloları ── */}
                        <Row>
                            <Col lg="4">
                                <Card>
                                    <CardBody>
                                        <CardTitle>Task'a Göre</CardTitle>
                                        <Table size="sm" responsive>
                                            <thead><tr><th>Task</th><th className="text-end">USD</th><th className="text-end">TRY</th></tr></thead>
                                            <tbody>
                                                {Object.entries(report.by_task || {}).map(([k, v]) => (
                                                    <tr key={k}><td>{k}</td><td className="text-end">{fmtUsd(v)}</td><td className="text-end text-success">{fmtTry(v, rate)}</td></tr>
                                                ))}
                                                {Object.keys(report.by_task || {}).length === 0 && (
                                                    <tr><td colSpan="3" className="text-muted">Veri yok</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col lg="4">
                                <Card>
                                    <CardBody>
                                        <CardTitle>Model'e Göre</CardTitle>
                                        <Table size="sm" responsive>
                                            <thead><tr><th>Model</th><th className="text-end">USD</th><th className="text-end">TRY</th></tr></thead>
                                            <tbody>
                                                {Object.entries(report.by_model || {}).map(([k, v]) => (
                                                    <tr key={k}><td>{k}</td><td className="text-end">{fmtUsd(v)}</td><td className="text-end text-success">{fmtTry(v, rate)}</td></tr>
                                                ))}
                                                {Object.keys(report.by_model || {}).length === 0 && (
                                                    <tr><td colSpan="3" className="text-muted">Veri yok</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col lg="4">
                                <Card>
                                    <CardBody>
                                        <CardTitle>Mode'a Göre</CardTitle>
                                        <Table size="sm" responsive>
                                            <thead><tr><th>Mode</th><th className="text-end">USD</th><th className="text-end">TRY</th></tr></thead>
                                            <tbody>
                                                {Object.entries(report.by_mode || {}).map(([k, v]) => (
                                                    <tr key={k}><td>{k}</td><td className="text-end">{fmtUsd(v)}</td><td className="text-end text-success">{fmtTry(v, rate)}</td></tr>
                                                ))}
                                                {Object.keys(report.by_mode || {}).length === 0 && (
                                                    <tr><td colSpan="3" className="text-muted">Veri yok</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {/* ── Top users ── */}
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody>
                                        <CardTitle>En Çok Tüketen Kullanıcılar (Top 10)</CardTitle>
                                        <Table responsive hover>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Kullanıcı</th>
                                                    <th className="text-end">Çağrı</th>
                                                    <th className="text-end">USD</th>
                                                    <th className="text-end">TRY</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(report.top_users || []).map((u, i) => {
                                                    const name = userNameById[String(u.user_id)] || `#${u.user_id}`;
                                                    return (
                                                        <tr key={u.user_id} style={{ cursor: "pointer" }}
                                                            onClick={() => pickUser({ id: u.user_id, name, email: "" })}>
                                                            <td>{i + 1}</td>
                                                            <td>
                                                                <div className="fw-semibold">{name}</div>
                                                                <div className="text-muted small">ID: {u.user_id}</div>
                                                            </td>
                                                            <td className="text-end">{u.calls}</td>
                                                            <td className="text-end">{fmtUsd(u.usd)}</td>
                                                            <td className="text-end text-success">{fmtTry(u.usd, rate)}</td>
                                                            <td className="text-end">
                                                                <Button size="sm" color="light">Filtrele</Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {(report.top_users || []).length === 0 && (
                                                    <tr><td colSpan="6" className="text-muted">Veri yok</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        </div>
    );
}
